import {
    Avatar,
    Button,
    Divider,
    FIPlus,
    Flexer,
    Header,
    Heading,
    Icon,
    Image,
    Menu,
    MenuDivider,
    MenuHeading,
    MenuItem,
    Popover,
    Portal,
    View,
    logger,
    stopEvent,
    useVisibility,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import {
    TbCheckbox,
    TbChevronDown,
    TbFilters,
    TbHash,
    TbLayoutGrid,
    TbLayoutGridAdd,
    TbMessage,
    TbMessageCircle,
    TbPlus,
    TbUserPlus,
} from 'react-icons/tb'
import { useParams } from 'react-router'
import { SlidePanel } from '../../components/slide-panel/slide-panel'
import { isRichTextBlank } from '../../helpers'
import { useWorkspaces } from '../../hooks/api.hooks'
import { WorkspaceMenu } from '../../menus/workspace.menu'
import { NewTaskModal } from '../../modals/new-task.modal'
import { useAppStore } from '../../store/app'
import { CreateChannel } from './create-channel'
import { CreateConversation } from './create-conversation'
import { CreateProject } from './create-project'
import { CreateTeam } from './create-team'

export const NavHeader = ({
    onSettings,
    onMembers,
    onAddWorkspace,
    onAddTeam,
    onAddProject,
    onAccount,
    onLabels,
    onHelp,
    onProjects,
}) => {
    const addModal = useVisibility()
    const { visible, show, hide } = useVisibility()
    const { workspaceId } = useParams()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>('')
    const [workspaces, setWorkspaces] = useState([])
    const workspace = useAppStore((state) => state.workspace)
    const { name, image } = workspace
    const { isOwner, isMember, isGuest } = useMemo(
        () => ({
            isOwner: workspace.role == 'Owner' || workspace.role == 'Admin',
            isMember: workspace.role == 'Member',
            isGuest: workspace.role == 'Guest',
        }),
        [workspace.role]
    )
    const { getAll } = useWorkspaces()
    const [panel, setPanel] = useState(0)
    const tools = useVisibility()
    const [type, setType] = useState('')
    const noDescription = useMemo(() => isRichTextBlank(workspace.description), [workspace.description])
    const showInfo = useMemo(() => isGuest && (!!image || !noDescription), [isGuest, image, noDescription])

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const workspaces = await getAll()
            setLoading(false)
            setWorkspaces(workspaces)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    useEffect(() => {
        if (visible) {
            fetchData()
            setPanel(0)
        }
    }, [visible])

    useEffect(() => {
        if (tools.visible) setPanel(0)
    }, [tools.visible])

    return (
        <>
            {addModal.visible && (
                <NewTaskModal
                    type="nav"
                    teamId={null}
                    onDismiss={addModal.hide}
                    defaultUsers={[]}
                    selectProject
                />
            )}
            <Header
                row
                bg="transparent"
                p="1.4rem 1.3rem 0.5rem 1.5rem"
                colorToken="base-500"
                border="none"
                width="100%">
                <View
                    row
                    gap={10}
                    width="100%">
                    {!isGuest && (
                        <Avatar
                            className="f-buttonize"
                            size="sm"
                            width={24}
                            height={24}
                            key={image}
                            src={image}
                            name={name}
                            onClick={(e) => (isOwner ? onSettings() : null)}
                            radius="var(--f-radius-sm)"
                            style={{
                                '--f-image-background-color': 'var(--f-color-surface-stronger)',
                                '--f-avatar-background-color': 'var(--f-color-surface-stronger)',
                            }}
                            imageProps={{ 
                                radius: 'var(--f-radius)',
                            }}
                        />
                    )}

                    <Heading
                        as="h5"
                        onClick={show}
                        className="nav-heading f-ellipsis f-buttonize">
                        {name}
                    </Heading>

                    <Popover
                        focusTrap
                        arrow
                        width={230}
                        portal={Portal}
                        isVisible={visible}
                        onDismiss={hide}
                        content={
                            <div
                                onMouseDown={stopEvent}
                                onClick={stopEvent}>
                                <SlidePanel panel={panel}>
                                    <WorkspaceMenu
                                        hide={hide}
                                        workspaces={workspaces}
                                        onLabels={onLabels}
                                        onProjects={() => {
                                            hide()
                                            onProjects()
                                        }}
                                        onSettings={() => {
                                            hide()
                                            onSettings()
                                        }}
                                        onMembers={() => {
                                            hide()
                                            onMembers()
                                        }}
                                        onAddTeam={() => setPanel(1)}
                                        onAccount={onAccount}
                                        onHelp={onHelp}
                                        onAddWorkspace={onAddWorkspace}
                                    />

                                    <CreateTeam
                                        onCreate={({ name, color }) => {
                                            onAddTeam({ name, color })
                                            setPanel(0)
                                            setTimeout(hide, 100)
                                        }}
                                        onBack={() => setPanel(0)}
                                    />
                                </SlidePanel>
                            </div>
                        }>
                        <Button
                            subtle
                            width={25}
                            height={25}
                            style={{ outlineWidth: 0 }}
                            className={visible ? undefined : 'reveal-on-hover-target'}
                            size="xs"
                            m="0 auto 0 0"
                            onClick={show}>
                            <Icon
                                icon={TbChevronDown}
                                size="sm"
                            />
                        </Button>
                    </Popover>

                    <Flexer />

                    <Popover
                        arrow
                        focusTrap
                        width={250}
                        portal={Portal}
                        isVisible={tools.visible}
                        onDismiss={tools.hide}
                        content={
                            <View width="100%">
                                <SlidePanel panel={panel}>
                                    <Menu
                                        shadow="none"
                                        width="100%"
                                        border="none">
                                        <MenuHeading>Create New</MenuHeading>
                                        <MenuItem
                                            prefix={<Icon icon={TbFilters} />}
                                            onClick={() => {
                                                setType('team')
                                                setPanel(1)
                                            }}>
                                            Team
                                        </MenuItem>
                                        <MenuItem
                                            prefix={<Icon icon={TbCheckbox} />}
                                            onClick={() => {
                                                tools.hide()
                                                addModal.show()
                                            }}>
                                            Task
                                        </MenuItem>
                                        <MenuItem
                                            prefix={<Icon icon={TbLayoutGridAdd} />}
                                            onClick={() => {
                                                setType('project')
                                                setPanel(1)
                                            }}>
                                            Project
                                        </MenuItem>
                                        <MenuItem
                                            prefix={<Icon icon={TbMessageCircle} />}
                                            onClick={() => {
                                                setType('channel')
                                                setPanel(1)
                                            }}>
                                            Channel
                                        </MenuItem>
                                        <MenuItem
                                            prefix={<Icon icon={TbMessage} />}
                                            onClick={() => {
                                                setType('conversation')
                                                setPanel(1)
                                            }}>
                                            Direct Message
                                        </MenuItem>
                                        {isOwner && (
                                            <MenuItem
                                                prefix={<Icon icon={TbUserPlus} />}
                                                onClick={() => {
                                                    tools.hide()
                                                    onMembers()
                                                }}>
                                                Workspace Member
                                            </MenuItem>
                                        )}
                                        <MenuDivider />
                                        <MenuItem
                                            prefix={<Icon icon={TbLayoutGrid} />}
                                            onClick={onProjects}>
                                            Browse Projects
                                        </MenuItem>
                                    </Menu>

                                    <div>
                                        {type == 'project' && (
                                            <CreateProject
                                                onCreate={({ name, color }) => {
                                                    onAddProject({ name, color })
                                                    setPanel(0)
                                                    setTimeout(tools.hide, 100)
                                                }}
                                                onBack={() => setPanel(0)}
                                            />
                                        )}

                                        {type == 'channel' && (
                                            <CreateChannel
                                                onCreate={({ name, color }) => {
                                                    onAddProject({ name, color, isChannel: true })
                                                    setPanel(0)
                                                    setTimeout(tools.hide, 100)
                                                }}
                                                onBack={() => setPanel(0)}
                                            />
                                        )}

                                        {type == 'conversation' && (
                                            <CreateConversation
                                                onCreate={({ users }) => {
                                                    onAddProject({
                                                        name: '',
                                                        color: undefined,
                                                        isChannel: true,
                                                        isPrivate: true,
                                                        isClosed: true,
                                                        users,
                                                    })
                                                    setPanel(0)
                                                    setTimeout(tools.hide, 100)
                                                }}
                                                onBack={() => setPanel(0)}
                                            />
                                        )}

                                        {type == 'team' && (
                                            <CreateTeam
                                                onCreate={({ name, color }) => {
                                                    onAddTeam({ name, color })
                                                    setPanel(0)
                                                    setTimeout(tools.hide, 100)
                                                }}
                                                onBack={() => setPanel(0)}
                                            />
                                        )}
                                    </div>
                                </SlidePanel>
                            </View>
                        }>
                        <Button
                            height={25}
                            width={25}
                            p={0}
                            onClick={tools.show}
                            display={isGuest ? 'none' : undefined}>
                            <Icon
                                icon={FIPlus}
                                style={{
                                    '--f-icon-sizing-md': '15px',
                                    '--f-icon-stroke-width-md': '2.5',
                                }}
                            />
                        </Button>
                    </Popover>
                </View>
            </Header>

            {showInfo && (
                <View
                    column
                    gap="10px"
                    width="100%"
                    p="0.5rem 1.3rem 0rem 1.5rem">
                    {!!image && (
                        <Image
                            width="100%"
                            height={100}
                            key={image}
                            src={image}
                        />
                    )}
                    {!noDescription && (
                        <div
                            className="f-text md"
                            dangerouslySetInnerHTML={{ __html: workspace.description }}
                        />
                    )}
                    <Divider m="0.4rem 0 0 0" />
                </View>
            )}
        </>
    )
}
